import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Products
export const getGuides = () => API.get('/products/guides');
export const getGuide = (id) => API.get(`/products/guides/${id}`);
export const getBundle = () => API.get('/products/bundle');
export const getFreebies = () => API.get('/products/freebies');
export const getFreebie = (id) => API.get(`/products/freebies/${id}`);
export const getTools = () => API.get('/tools');
export const getTool = (id) => API.get(`/tools/${id}`);
export const getSEOPages = () => API.get('/seo-pages');

// Email subscription
export const subscribe = (data) => API.post('/subscribe', data);

// Checkout
export const createCheckoutSession = (data) => API.post('/checkout/create-session', data);
export const getCheckoutStatus = (sessionId) => API.get(`/checkout/status/${sessionId}`);

// Promo codes
export const validatePromoCode = (data) => API.post('/promo/validate', data);

// Tools
export const submitTool = (data) => API.post('/tools/submit', data);
export const generateToolReport = (data) => API.post('/tools/generate-report', data);
export const getRecentToolResults = (email, sessionId, limit = 5) =>
  API.get('/tools/reports/recent', { params: { email, session_id: sessionId, limit } });
export const getToolResult = (resultId, email, sessionId) =>
  API.get(`/tools/results/${resultId}`, { params: { email, session_id: sessionId } });
export const adoptToolResult = (resultId, data) =>
  API.post(`/tools/results/${resultId}/adopt`, data);
export const askKaiAboutResult = (resultId, data) =>
  API.post(`/tools/results/${resultId}/ask-kai`, data);
export const askKaiAboutArticle = (data) =>
  API.post('/tools/ask-kai-about-article', data);
export const sendResultRecoveryLink = (resultId, data) =>
  API.post(`/tools/results/${resultId}/recovery-link`, data);

// Analytics
// Analytics — UNIFIED SCHEMA (V16+): { event_name, properties }
// Backend /analytics/track normalizes both shapes; frontend should always send the new one.
export const trackEvent = (eventName, properties) =>
  API.post('/analytics/track', { event_name: eventName, properties: properties || {} });

// trackEventBeacon — for events fired immediately before a hard navigation
// (React Router <Link>, window.location, etc). navigator.sendBeacon survives
// page unload; a normal axios POST can be aborted mid-flight. Falls back to a
// keepalive fetch if sendBeacon is unavailable, and finally to the async POST.
export const trackEventBeacon = (eventName, properties) => {
  const url = `${BACKEND_URL}/api/analytics/track`;
  const body = JSON.stringify({ event_name: eventName, properties: properties || {} });
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return true;
    }
  } catch { /* fall through */ }
  try {
    if (typeof fetch === 'function') {
      // keepalive lets the request finish even if the tab is navigating.
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
      return true;
    }
  } catch { /* fall through */ }
  // Last-resort fallback — will race with navigation.
  API.post('/analytics/track', { event_name: eventName, properties: properties || {} }).catch(() => {});
  return false;
};
export const getAnalyticsDashboard = () => API.get('/analytics/dashboard');

// Content generation
export const generateWeeklyContent = () => API.post('/content/generate-weekly');
export const getGeneratedArticles = () => API.get('/content/articles');
export const getGeneratedArticle = (slug) => API.get(`/content/articles/${slug}`);
export const getContentStatus = () => API.get('/content/status');

// Kai AI Coach
export const kaiVerifySkool = (data) => API.post('/kai/verify-skool', data);
export const kaiRequestCode = (data) => API.post('/kai/request-code', data);
export const kaiVerifyCode = (data) => API.post('/kai/verify-code', data);
export const kaiCheckSession = (data) => API.post('/kai/check-session', data);
export const kaiChat = (data) => API.post('/kai/chat', data);
export const kaiFeedback = (data) => API.post('/kai/feedback', data);
export const kaiReset = (data) => API.post('/kai/reset', data);
export const kaiMembership = (data) => API.post('/kai/membership', data);
export const kaiPricing = () => API.get('/kai/pricing');
export const kaiFoundingSeats = () => API.get('/kai/founding-seats');
export const kaiCheckout = (data) => API.post('/kai/checkout', data);
export const kaiCheckoutPasses = (data) => API.post('/kai/checkout-passes', data);
export const kaiBillingPortal = (data) => API.post('/kai/billing-portal', data);
export const kaiUpgradeStatus = (sid) => API.get(`/kai/upgrade-status/${sid}`);
export const kaiAdminAnalytics = (pw) => API.get('/admin/kai/analytics', { headers: { 'X-Admin-Password': pw } });
export const kaiAdminConversations = (pw) => API.get('/admin/kai/conversations', { headers: { 'X-Admin-Password': pw } });
export const kaiAdminConversationDetail = (sid, pw) => API.get(`/admin/kai/conversation/${sid}`, { headers: { 'X-Admin-Password': pw } });
export const kaiAdminMembers = (pw) => API.get('/admin/kai/members', { headers: { 'X-Admin-Password': pw } });
export const kaiAdminGrantPaid = (data, pw) => API.post('/admin/kai/grant-paid', data, { headers: { 'X-Admin-Password': pw } });
export const kaiAdminCompanionStats = (pw) => API.get('/admin/kai/companion-stats', { headers: { 'X-Admin-Password': pw } });
export const kaiAdminProactiveStatus = (pw) => API.get('/admin/kai/proactive-status', { headers: { 'X-Admin-Password': pw } });
export const kaiAdminProactiveStatusSet = (data, pw) => API.post('/admin/kai/proactive-status', data, { headers: { 'X-Admin-Password': pw } });

// Kai Companion (emotional / proactive / KAI-led check-in)
export const kaiProactiveList = (sessionId, email) => API.get('/kai/proactive', { params: { session_id: sessionId, email } });
export const kaiProactiveDismiss = (data) => API.post('/kai/proactive/dismiss', data);
export const kaiCompanionCheckin = (data) => API.post('/kai/checkin', data);

// Kai Companion — Phase 2 (recovery / timeline / patterns / moments)
export const kaiRecoveryState = (sessionId, email) => API.get('/kai/recovery-state', { params: { session_id: sessionId, email } });
export const kaiRecoveryResolve = (data) => API.post('/kai/recovery/resolve', data);
export const kaiTimeline = (sessionId, email, limit = 30) => API.get('/kai/timeline', { params: { session_id: sessionId, email, limit } });
export const kaiCompanionMoments = (sessionId, email) => API.get('/kai/companion-moments', { params: { session_id: sessionId, email } });
export const kaiBehaviorPatterns = (sessionId, email) => API.get('/kai/behavior-patterns', { params: { session_id: sessionId, email } });

export default API;
